import React from 'react';
import {MapPin, Car, DollarSign, Clock, Sun} from 'lucide-react';

export default function Anaheim() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/1268871/pexels-photo-1268871.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Anaheim"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/80 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl font-bold text-white mb-2">Anaheim, California</h1>
            <p className="text-xl text-white/90">Home of the Original Disneyland</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-lg p-4 shadow-md">
            <Clock className="w-6 h-6 text-[#4A90E2] mb-2" />
            <p className="text-sm text-gray-500">Recommended Stay</p>
            <p className="font-semibold text-gray-800">3-4 days</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md">
            <DollarSign className="w-6 h-6 text-[#F7B32B] mb-2" />
            <p className="text-sm text-gray-500">Daily Budget</p>
            <p className="font-semibold text-gray-800">$200-350</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md">
            <Car className="w-6 h-6 text-[#FF6B35] mb-2" />
            <p className="text-sm text-gray-500">Airport</p>
            <p className="font-semibold text-gray-800">30 min from LAX</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md">
            <Sun className="w-6 h-6 text-[#0A2540] mb-2" />
            <p className="text-sm text-gray-500">Climate</p>
            <p className="font-semibold text-gray-800">Sunny & Mild</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Anaheim</h2>
            <p className="text-gray-600 mb-4">
              Anaheim is synonymous with Disneyland Resort – Walt Disney's original "Happiest Place on Earth." Located in sunny Southern California, it offers the perfect blend of world-class theme parks, nearby beaches, and California culture.
            </p>
            <p className="text-gray-600 mb-8">
              Beyond Disney, Anaheim provides easy access to Los Angeles attractions, Orange County beaches, and year-round pleasant weather.
            </p>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Disneyland Resort</h3>
            <div className="space-y-4 mb-8">
              {[
                { name: 'Disneyland Park', desc: 'The original Disney park with classic attractions and nostalgic charm' },
                { name: 'Disney California Adventure', desc: 'Modern park featuring Marvel, Pixar, and California-themed lands' },
                { name: 'Downtown Disney', desc: 'Shopping, dining, entertainment – no ticket required' }
              ].map((area, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35]">
                  <h4 className="font-semibold text-gray-800 mb-1">{area.name}</h4>
                  <p className="text-sm text-gray-600">{area.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Must-Do Attractions</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Sleeping Beauty Castle:</strong> Iconic Disney landmark (walkthrough inside!)</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Star Wars: Rise of the Resistance:</strong> Revolutionary immersive experience</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Guardians of the Galaxy: Mission Breakout:</strong> Thrilling drop tower</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Matterhorn Bobsleds:</strong> First tubular steel roller coaster in the world</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Radiator Springs Racers:</strong> High-speed Cars-themed adventure</span>
              </li>
            </ul>
          </div>

          <div>
            <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
              <h3 className="text-xl font-bold text-[#0A2540] mb-4">Insider Tips</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li>✓ Both parks are walkable from each other</li>
                <li>✓ Park Hopper ticket lets you visit both in one day</li>
                <li>✓ Arrive 30 min before rope drop</li>
                <li>✓ Use Disney Genie+ for shorter waits</li>
                <li>✓ Watch fireworks from Main Street USA</li>
                <li>✓ Many "Good Neighbor" hotels within walking distance</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#FF6B35] to-[#F7B32B] rounded-xl p-6 text-white mb-6">
              <Sun className="w-8 h-8 mb-3" />
              <h3 className="text-xl font-bold mb-2">Nearby Attractions</h3>
              <ul className="space-y-2 text-sm">
                <li>• Huntington Beach (20 min)</li>
                <li>• Knott's Berry Farm (15 min)</li>
                <li>• Los Angeles (45 min)</li>
                <li>• Hollywood & Santa Monica (1 hr)</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#4A90E2] to-[#0A2540] rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-2">Best Time to Visit</h3>
              <ul className="space-y-2 text-sm">
                <li><strong>Least Crowded:</strong> Jan-Feb, mid-Sep to Nov</li>
                <li><strong>Special Events:</strong> Halloween (Sep-Oct), Christmas (Nov-Jan)</li>
                <li><strong>Weather:</strong> Perfect year-round (avoid July-Aug heat)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
